<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import CincelAPI from 'dashboard/api/integrations/cincel';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CincelSignatureTemplates from './CincelSignatureTemplates.vue';

const APP_ID = 'cincel';

const store = useStore();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationLoaded = ref(false);
const email = ref('');
const otp = ref('');
const otpRequested = ref(false);
const isRequestingOtp = ref(false);
const isVerifying = ref(false);
// Connect method: 'pat' (recommended — self-refreshing, no re-login) or 'otp' (email code).
const authMethod = ref('pat');
const pat = ref('');
const isConnectingPat = ref(false);
const teamUuid = ref('');
const folderUuid = ref('');
const teams = ref([]);
const folders = ref([]);
const disconnectDialogRef = ref(null);

const integration = useFunctionGetter('integrations/getIntegration', APP_ID);

const hook = computed(() => integration.value?.hooks?.[0]);
const isConnected = computed(() => Boolean(hook.value));

const syncFromHook = () => {
  const settings = hook.value?.settings || {};
  email.value = settings.email || '';
  teamUuid.value = settings.team_uuid || '';
  folderUuid.value = settings.default_folder_uuid || '';
};

watch(hook, syncFromHook, { immediate: true });

// Step 1: email a one-time code.
const requestOtp = async () => {
  isRequestingOtp.value = true;
  try {
    await CincelAPI.requestOtp({ email: email.value.trim() });
    otpRequested.value = true;
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.CONNECT.OTP_SENT'));
  } catch (error) {
    const detail = error?.response?.data?.error;
    useAlert(detail || t('INTEGRATION_SETTINGS.CINCEL.CONNECT.OTP_ERROR'));
  } finally {
    isRequestingOtp.value = false;
  }
};

// Step 2: exchange the code for a JWT and persist the hook.
const verifyOtp = async () => {
  isVerifying.value = true;
  try {
    await CincelAPI.verifyOtp({
      email: email.value.trim(),
      otp: otp.value.trim(),
    });
    otp.value = '';
    otpRequested.value = false;
    await store.dispatch('integrations/get', APP_ID);
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.CONNECT.SUCCESS'));
    await loadTeams();
  } catch (error) {
    const detail = error?.response?.data?.error;
    useAlert(detail || t('INTEGRATION_SETTINGS.CINCEL.CONNECT.ERROR'));
  } finally {
    isVerifying.value = false;
  }
};

// Connect with a Personal Access Token: stored encrypted, the JWT refreshes itself, so the
// admin connects once and never signs in again.
const connectWithPat = async () => {
  isConnectingPat.value = true;
  try {
    await CincelAPI.connectWithPat({ pat: pat.value.trim() });
    pat.value = '';
    await store.dispatch('integrations/get', APP_ID);
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.CONNECT.SUCCESS'));
    await loadTeams();
  } catch (error) {
    const detail = error?.response?.data?.error;
    useAlert(detail || t('INTEGRATION_SETTINGS.CINCEL.CONNECT.PAT_ERROR'));
  } finally {
    isConnectingPat.value = false;
  }
};

const loadTeams = async () => {
  if (!isConnected.value) return;
  try {
    const { data } = await CincelAPI.getTeams();
    teams.value = Array.isArray(data) ? data : [];
    if (teamUuid.value) await loadFolders();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.TEAM.ERROR'));
  }
};

const loadFolders = async () => {
  if (!teamUuid.value) return;
  try {
    const { data } = await CincelAPI.getFolders(teamUuid.value);
    folders.value = Array.isArray(data) ? data : [];
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.FOLDER.ERROR'));
  }
};

const onTeamChange = async () => {
  folderUuid.value = '';
  folders.value = [];
  await persistTeamFolder();
  await loadFolders();
};

// Persists team/folder server-side (merges, never wipes) and auto-registers the webhook.
const persistTeamFolder = async () => {
  try {
    await CincelAPI.saveTeamFolder({
      teamUuid: teamUuid.value,
      folderUuid: folderUuid.value,
    });
    await store.dispatch('integrations/get', APP_ID);
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SAVE.ERROR'));
  }
};

const openDisconnectDialog = () => disconnectDialogRef.value?.open();

const disconnect = async () => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: APP_ID,
      hookId: hook.value.id,
    });
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.ERROR'));
  }
};

onMounted(async () => {
  await store.dispatch('integrations/get', APP_ID);
  integrationLoaded.value = true;
  await loadTeams();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div v-if="integrationLoaded" class="flex flex-col gap-6">
      <!-- Header -->
      <div
        class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow gap-6"
      >
        <div class="flex-1">
          <h3 class="mb-1 text-xl font-medium text-n-slate-12">
            {{ integration.name }}
          </h3>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ replaceInstallationName(integration.description) }}
          </p>
        </div>
        <Button
          v-if="isConnected"
          faded
          ruby
          :label="t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.BUTTON')"
          @click="openDisconnectDialog"
        />
      </div>

      <!-- Connection (passwordless email OTP) -->
      <div
        class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div>
          <h4 class="text-lg font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.HEADER') }}
          </h4>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.DESCRIPTION') }}
          </p>
        </div>

        <!-- Connected state -->
        <p v-if="isConnected" class="text-sm text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.CONNECT.CONNECTED_AS', { email }) }}
        </p>

        <!-- Connect flow -->
        <template v-else>
          <!-- PAT connect (recommended — connect once, self-refreshing) -->
          <template v-if="authMethod === 'pat'">
            <Input
              v-model="pat"
              type="password"
              :label="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.PAT.LABEL')"
              :placeholder="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.PAT.PLACEHOLDER')"
              :help-text="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.PAT.HELP')"
            />
            <div class="flex items-center justify-between gap-2">
              <button
                type="button"
                class="text-sm text-n-slate-11 hover:underline"
                @click="authMethod = 'otp'"
              >
                {{ t('INTEGRATION_SETTINGS.CINCEL.CONNECT.USE_OTP') }}
              </button>
              <Button
                blue
                :is-loading="isConnectingPat"
                :label="t('INTEGRATION_SETTINGS.CINCEL.CONNECT.BUTTON')"
                :disabled="!pat"
                @click="connectWithPat"
              />
            </div>
          </template>

          <!-- Email-OTP connect (fallback) -->
          <template v-else>
            <Input
              v-model="email"
              type="email"
              :label="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.EMAIL.LABEL')"
              :placeholder="
                t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.EMAIL.PLACEHOLDER')
              "
            />
            <Input
              v-if="otpRequested"
              v-model="otp"
              :label="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.OTP.LABEL')"
              :placeholder="t('INTEGRATION_SETTINGS.CINCEL.CREDENTIALS.OTP.PLACEHOLDER')"
            />
            <div class="flex items-center justify-between gap-2">
              <button
                type="button"
                class="text-sm text-n-slate-11 hover:underline"
                @click="authMethod = 'pat'"
              >
                {{ t('INTEGRATION_SETTINGS.CINCEL.CONNECT.USE_PAT') }}
              </button>
              <div class="flex justify-end gap-2">
                <Button
                  faded
                  slate
                  :is-loading="isRequestingOtp"
                  :label="
                    otpRequested
                      ? t('INTEGRATION_SETTINGS.CINCEL.CONNECT.RESEND_CODE')
                      : t('INTEGRATION_SETTINGS.CINCEL.CONNECT.SEND_CODE')
                  "
                  :disabled="!email"
                  @click="requestOtp"
                />
                <Button
                  v-if="otpRequested"
                  blue
                  :is-loading="isVerifying"
                  :label="t('INTEGRATION_SETTINGS.CINCEL.CONNECT.BUTTON')"
                  :disabled="!otp"
                  @click="verifyOtp"
                />
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- Team & Folder -->
      <div
        v-if="isConnected"
        class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div>
          <h4 class="text-lg font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.HEADER') }}
          </h4>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.DESCRIPTION') }}
          </p>
        </div>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.TEAM.LABEL') }}
          <select
            v-model="teamUuid"
            class="mt-1 block w-full rounded-md border border-n-weak bg-n-alpha-3 text-n-slate-12 text-sm py-2 px-3"
            @change="onTeamChange"
          >
            <option value="">
              {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.TEAM.PLACEHOLDER') }}
            </option>
            <option v-for="team in teams" :key="team.uuid" :value="team.uuid">
              {{ team.name || team.uuid }}
            </option>
          </select>
        </label>
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.FOLDER.LABEL') }}
          <select
            v-model="folderUuid"
            class="mt-1 block w-full rounded-md border border-n-weak bg-n-alpha-3 text-n-slate-12 text-sm py-2 px-3"
            @change="persistTeamFolder"
          >
            <option value="">
              {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.FOLDER.PLACEHOLDER') }}
            </option>
            <option
              v-for="folder in folders"
              :key="folder.uuid"
              :value="folder.uuid"
            >
              {{ folder.name || folder.uuid }}
            </option>
          </select>
        </label>
        <p class="text-n-slate-11 text-xs leading-5">
          {{ t('INTEGRATION_SETTINGS.CINCEL.TEAM_FOLDER.WEBHOOK_AUTO') }}
        </p>
      </div>

      <!-- Signature templates + connect to forms -->
      <CincelSignatureTemplates
        v-if="isConnected"
        :team-uuid="teamUuid"
      />
    </div>

    <Dialog
      ref="disconnectDialogRef"
      type="alert"
      :title="t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.TITLE')"
      :description="t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.MESSAGE')"
      :confirm-button-label="t('INTEGRATION_SETTINGS.CINCEL.DISCONNECT.CONFIRM')"
      @confirm="disconnect"
    />
  </div>
</template>
